/**
 * @file
 * Old AMD key: `ac/jira/issue`
 */

/* eslint-disable no-console */

import { fg } from '@atlassian/jira-feature-gating';
import { asyncWrmRequire } from '@atlassian/jira-wrm';
import type { ConnectCallback } from '../../../types';
import type { CreatedIssue, CreatedIssueDetails } from './types';
/**
 * Open the quick create issue dialog. The dialog fields may be pre-filled with supplied data. A callback will be invoked when the dialog is closed and will include an array of issues created.
 * @memberof module:Jira
 * @method openCreateIssueDialog
 * @param {Function} callback - invoked when dialog is closed, takes a single parameter - array of issues created
 * @param {Object} params - contains data to pre-fill the dialog with
 * @param {number} params.pid - Project to pre-fill the dialog with
 * @param {number} params.issueType - Issue type to pre-fill the dialog with
 * @param {Object} params.fields - Optional data to pre-fill the dialog with, you can find out field key by checking it's html 'name' attribute.
 * @noDemo
 * @example
 * AP.jira.openCreateIssueDialog(function(issues){
 *   alert(issues[0]['fields']['summary']);
 * }, {
 *   pid: 10000,
 *   issueType: 1,
 *   fields : {
 *     summary: "Hello World",
 *     environment : "My environment",
 *     priority : 2,
 *     assignee: "tom",
 *     reporter: "bob",
 *     labels : ["Mylabel","MyOtherLabel"],
 *     description : "My first Issue",
 *     duedate : "11/Oct/16",
 *     fixVersions : 10001,
 *     versions : 10000,
 *     components : [10005, 10006],
 *     timetracking_originalestimate: "2w",
 *     timetracking_remainingestimate: "3d",
 *     worklog_activate: true,
 *     worklog_timeLogged: "2"
 *   }
 * });
 */
export const openCreateIssueDialog = (
	fields: unknown,
	callback: ConnectCallback<(issues: CreatedIssueDetails[]) => void>,
) => {
	if (fg('ecosystem_api_to_show_modern_gic')) {
		document.dispatchEvent(
			new CustomEvent('openIssueCreateDialog', {
				detail: {
					triggerPointKey: 'connect-app-ecosystem',
					payload: {
						isEcosystemAPI: true,
						prefilledFields: fields
							? // @ts-expect-error fields is of type unknown
								{ pid: fields?.pid, issuetype: fields?.issueType, ...fields?.fields }
							: {},
					},
				},
			}),
		);

		let sanitizedIssues: CreatedIssueDetails[] = [];
		const createCallback = (_event: unknown, issues: CreatedIssue[]) => {
			sanitizedIssues = issues.map((issue) => issue.createdIssueDetails);
		};

		JIRA.one('QuickCreateIssue.sessionComplete', createCallback);
		if (typeof callback === 'function') callback.call({}, sanitizedIssues);
		return;
	}

	asyncWrmRequire(['wrc!com.atlassian.jira.jira-quick-edit-plugin:quick-create-issue']).then(() => {
		if (!JIRA || !JIRA.Forms || !JIRA.Forms.createCreateIssueForm) {
			console.warn('Connect: Create issue form is not available');
			return;
		}
		const dialog = JIRA.Forms.createCreateIssueForm(fields).asDialog({
			trigger: document.createElement('a'),
			id: 'create-issue-dialog',
			windowTitle: document.title.replace(/ - Jira$/, ''),
			shouldLoadHeritageFormDialogContext: true,
			triggerPointKey: 'ecosystemApiCreateIssue',
		});

		dialog.show();

		let sanitizedIssues: CreatedIssueDetails[] = [];
		const createCallback = (_event: unknown, issues: CreatedIssue[]) => {
			sanitizedIssues = issues.map((issue) => issue.createdIssueDetails);
		};

		JIRA.one('QuickCreateIssue.sessionComplete', createCallback);
		dialog.bind('Dialog.hide', () => {
			if (typeof callback === 'function') {
				callback.call({}, sanitizedIssues);
			}
		});
	});
};

type AsyncModalsModule = {
	showConnectIssueModal?: (options: {
		issueKey: string;
		onCancel: (value: string) => void;
		onError: (value: string) => void;
	}) => void;
};

/**
 * Opens the issue dialog for an issue. A callback is invoked when the issue dialog is closed or an error occurs when opening the issue dialog.
 * @memberof module:Jira
 * @method openIssueDialog
 * @param {String} issueKey - The key of the issue.
 * @param {Function} callback - Invoked when the dialog is closed. Takes a single parameter, either `issueKey` or, if the issue dialog isn't opened, an error key.
 * it is impossible to open a issue dialog.
 * @noDemo
 * @example
 * AP.jira.openIssueDialog(
 *  'DEMO-1',
 *  function(){
 *    alert('Closed!');
 *  }
 * );
 */
export const openIssueDialog = (
	issueKey: string,
	callback: ConnectCallback<(issueKeyOrError: string) => void>,
) => {
	asyncWrmRequire(['wrc!com.atlassian.jira.jira-atlaskit-plugin:async-modals']).then(() => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const modalsPromise = window.require('jira/async-modals') as Promise<AsyncModalsModule>;

		modalsPromise.then((modals) => {
			if (!modals.showConnectIssueModal) {
				console.warn('Connect: open issue dialog is not available');
				callback('OPEN_ISSUE_DIALOG_IS_NOT_AVAILABLE');
				return;
			}
			modals.showConnectIssueModal({
				issueKey,
				onCancel: callback,
				onError: callback,
			});
		});
	});
};
